import { useStoryblokState } from '@cinch-nx/data-storyblok'
import {
  formatStoreToSchema,
  Head,
  JsonLdScript,
  renderWithResolver,
} from '@cinch-nx/landing-ui-shared'
import { Wrapper } from '@cinch-nx/shared-ui'
import cx from 'classnames'

import { HowItWorks, StoreIntro } from './components'
import { StoreDetailsWrapper } from './components/store-details-wrapper'
import { StoreFaqs } from './components/store-faqs'
import { WhyCinch } from './components/why-cinch'
import {
  generateFallbackDescription,
  generateFallbackTitle,
} from './data/landing-store-feature.data'
import type { LandingFeatureStoreProps } from './get-static-props'
import styles from './landing-feature-store-page.module.css'
import { capitaliseFirstLetter } from './utils/capitalise-first-letter'

export function LandingFeatureStorePage({
  story,
  generatedAt,
  preview,
  storeType,
}: LandingFeatureStoreProps) {
  const { content } = useStoryblokState({ story, enableBridge: preview })
  const {
    storeIntro,
    storeHowCinchWorksComponent,
    storeFAQSection,
    mapInformation,
    address,
    phoneNumbers,
    openingHoursList,
    storeHero,
    storeWhyBuy,
  } = content
  const SEO = content?.SEO?.[0]

  const storeIntroTitle = storeIntro[0].title
  const storeIntroText = renderWithResolver(storeIntro[0].body)

  const howItWorksTitle = storeHowCinchWorksComponent[0].title
  const howItWorksCta = storeHowCinchWorksComponent[0].cta?.[0]
  const howCinchWorksList = storeHowCinchWorksComponent[0].howCinchWorksList
  const { titleAndText: whyBuyContent } = storeWhyBuy[0]

  const storeSlug = story.slug
  const storeName = capitaliseFirstLetter(storeSlug)
  const [storeAddress] = address
  const storeImageUrl = storeHero[0].image.filename

  const { title: faqTitle, faqs } = storeFAQSection[0]

  return (
    <>
      <JsonLdScript
        schema={formatStoreToSchema({
          storeSlug,
          storeAddress,
          mapInformation,
          phoneNumbers,
          openingHoursList,
          storeImageUrl,
        })}
        tagId="automotive business"
      />
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle={generateFallbackTitle(storeName)}
        fallbackDescription={generateFallbackDescription(storeName)}
      />
      <article className={styles.root}>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreIntro
            title={storeIntroTitle}
            introText={storeIntroText}
            slug={storeSlug}
            storeName={storeName}
          />
        </Wrapper>
        <Wrapper
          backgroundColor="#f2f5ff"
          className={{ container: styles.howItWorksWrapper }}
          isSlim
        >
          <HowItWorks
            title={howItWorksTitle}
            howCinchWorksList={howCinchWorksList}
            slug={storeSlug}
            storeName={story.name}
            storeType={storeType}
            cta={howItWorksCta}
          />
        </Wrapper>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreDetailsWrapper
            slug={storeName}
            latitude={Number.parseFloat(mapInformation[0]?.latitude)}
            longitude={Number.parseFloat(mapInformation[0]?.longitude)}
            address={storeAddress}
            phoneNumbers={phoneNumbers}
            openingHours={openingHoursList}
          />
        </Wrapper>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreFaqs title={faqTitle} faqs={faqs} />
        </Wrapper>
        <Wrapper
          backgroundColor="#f8f6ff"
          className={{
            container: cx(styles.storeWrapper, styles.whyCinchWrapper),
            inner: styles.storeWrapperInner,
          }}
        >
          <WhyCinch whyBuyContent={whyBuyContent} />
        </Wrapper>
      </article>
    </>
  )
}

export default LandingFeatureStorePage
